import { importPeerPublicKey } from './chatCryptoKeys';

function bytesToB64(u8: Uint8Array): string {
  let s = '';
  for (let i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
  return btoa(s);
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** AES-256-GCM для режима «только локально»: HKDF от мастер-ключа 32 байта + threadId. */
export async function deriveLocalOnlyThreadAesKey(
  masterKey32: Uint8Array,
  threadId: string
): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey('raw', masterKey32, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode(threadId),
      info: new TextEncoder().encode('duvpost-local-only-v1'),
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/** Общий AES-256-GCM ключ чата из ECDH(P-256) + HKDF. */
export async function deriveChatAesKey(
  myPrivateKey: CryptoKey,
  peerPublicJwk: JsonWebKey,
  threadId: string
): Promise<CryptoKey> {
  const peerPublic = await importPeerPublicKey(peerPublicJwk);
  const bits = await crypto.subtle.deriveBits(
    { name: 'ECDH', public: peerPublic },
    myPrivateKey,
    256
  );
  const ikm = new Uint8Array(bits);
  const baseKey = await crypto.subtle.importKey('raw', ikm, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new TextEncoder().encode(threadId),
      info: new TextEncoder().encode('duvpost-chat-v1'),
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export async function encryptChatPlaintext(aesKey: CryptoKey, plaintext: string): Promise<{
  cipherTextB64: string;
  ivB64: string;
}> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    new TextEncoder().encode(plaintext)
  );
  return {
    cipherTextB64: bytesToB64(new Uint8Array(ct)),
    ivB64: bytesToB64(iv),
  };
}

export async function decryptChatPayload(
  aesKey: CryptoKey,
  cipherTextB64: string,
  ivB64: string
): Promise<string> {
  const iv = b64ToBytes(ivB64);
  const ct = b64ToBytes(cipherTextB64);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aesKey, ct);
  return new TextDecoder().decode(pt);
}
